// ── Friet Fighter — cinematic FX (render-only) ──
// Swoosh-trails + particle-bursts voor de super-cinematics (swoosh langs de
// prikker, kruimel/stoom-burst uit de friteuse, mie die alle kanten opspat).
// Scene-agnostisch: elke FX hangt z'n eigen `group` op in welke scene dan ook,
// zodat zowel de game-director als het dev-lab dezelfde FX kunnen draaien.

import * as THREE from 'three';

export type BurstKind = 'crumb' | 'noodle' | 'steam' | 'sauce' | 'fire';
export type BurstSpread = 'radial' | 'up' | 'jet';

/** Eén burst-trigger op swing-fase u. */
export interface FxBurst {
  u: number;
  kind: BurstKind;
  count: number;
  speed: number;
  spread: BurstSpread;
  color?: number;
  dir?: [number, number, number]; // voor spread:'jet' — straal-richting (wereld)
}

/** FX-spec per cinematic (declaratief; de runner voert 'm uit). */
export interface FxSpec {
  // trail volgt standaard de prop-tip; met `bone` volgt 'ie i.p.v. een bone (bv. de vuist).
  trail?: { color: number; width: number; bone?: string };
  bursts?: FxBurst[];
  originBone?: string; // bone voor de burst-oorsprong (val terug op een vast punt)
}

// Gedeelde geometrie/material per particle-soort (geen per-spawn allocatie van geo).
const crumbGeo = new THREE.BoxGeometry(0.05, 0.05, 0.05);
const noodleGeo = new THREE.CylinderGeometry(0.018, 0.018, 0.26, 6);
const steamGeo = new THREE.SphereGeometry(0.13, 8, 6);
const sauceGeo = new THREE.SphereGeometry(0.09, 8, 6); // saus-blob (glanzend, nat)
const fireGeo = new THREE.SphereGeometry(0.11, 8, 6); // vuur/kaas-spat (gloeiend)

interface Particle {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  life: number;
  max: number;
  baseScale: number;
  baseOpacity: number;
  steam: boolean;
  grav: number;
}

const GRAV = 7.5;
const FIRE_GRAV = 1.2; // vuur stijgt/zweeft → veel minder zwaartekracht
const UP = new THREE.Vector3(0, 1, 0);
const SIDE = new THREE.Vector3(1, 0, 0);

/** Een burst van particles vanaf een oorsprong; beweegt, valt, vervaagt, verdwijnt. */
export class Burst {
  readonly group = new THREE.Group();
  private parts: Particle[] = [];
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();
  private readonly tmp3 = new THREE.Vector3();

  /** Spawn `count` particles op `origin`. Index varieert de richting (deterministisch). */
  spawn(origin: THREE.Vector3, b: FxBurst, seed = 0): void {
    const isFire = b.kind === 'fire';
    const color =
      b.color ??
      (b.kind === 'steam' ? 0xeeeeee : b.kind === 'noodle' ? 0xd9c060 : b.kind === 'sauce' ? 0xc81e14 : isFire ? 0xffa030 : 0xc98a3a);
    const geo =
      b.kind === 'noodle' ? noodleGeo : b.kind === 'steam' ? steamGeo : b.kind === 'sauce' ? sauceGeo : isFire ? fireGeo : crumbGeo;
    // Jet (gerichte kegel): basis-richting + twee perpendiculaire assen vooraf.
    const base = this.tmp2.set(b.dir?.[0] ?? 0, b.dir?.[1] ?? 0, b.dir?.[2] ?? 1).normalize();
    const p1 = this.tmp3.copy(Math.abs(base.y) < 0.9 ? UP : SIDE).cross(base).normalize();
    const p2 = base.clone().cross(p1);
    for (let i = 0; i < b.count; i++) {
      // Vuur = gloeiend (additief, ongelit); de rest = standaard belicht.
      const mat = isFire
        ? new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1, depthWrite: false, blending: THREE.AdditiveBlending })
        : new THREE.MeshStandardMaterial({
            color,
            roughness: b.kind === 'steam' ? 1 : b.kind === 'sauce' ? 0.3 : 0.7,
            transparent: true,
            opacity: b.kind === 'steam' ? 0.28 : 1,
            emissive: b.kind === 'crumb' ? 0x3a1e08 : 0x000000,
          });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(origin);
      const sp = b.speed * (0.6 + 0.4 * frac(i * 0.53));
      const vel = new THREE.Vector3();
      if (b.spread === 'jet') {
        const jx = (frac(i * 0.37) - 0.5) * 0.6; // smalle kegel rond de straal-richting
        const jy = (frac(i * 0.61) - 0.5) * 0.6;
        vel.copy(base).addScaledVector(p1, jx).addScaledVector(p2, jy).normalize().multiplyScalar(sp);
      } else {
        const a = (i * 2.39996 + seed * 0.7) % (Math.PI * 2); // gulden hoek → spreiding
        const upBias = b.spread === 'up' ? 0.55 + 0.45 * frac(i * 0.37) : frac(i * 0.61) * 2 - 1;
        const r = Math.sqrt(Math.max(0, 1 - upBias * upBias));
        vel.set(Math.cos(a) * r, upBias, Math.sin(a) * r).multiplyScalar(sp);
        if (b.spread === 'up') vel.y += b.speed * 0.5;
      }
      const baseScale =
        b.kind === 'steam'
          ? 0.6 + frac(i * 0.29)
          : b.kind === 'sauce'
            ? 0.55 + frac(i * 0.43) * 1.1
            : isFire
              ? 0.7 + frac(i * 0.43) * 0.8
              : 0.7 + frac(i * 0.43) * 0.6;
      mesh.scale.setScalar(baseScale);
      this.group.add(mesh);
      this.parts.push({
        mesh,
        vel,
        spin: new THREE.Vector3((frac(i * 0.11) - 0.5) * 12, (frac(i * 0.27) - 0.5) * 12, (frac(i * 0.51) - 0.5) * 12),
        life: 0,
        max: b.kind === 'steam' ? 0.9 : isFire ? 0.45 + frac(i * 0.19) * 0.3 : 0.7 + frac(i * 0.19) * 0.4,
        baseScale,
        baseOpacity: b.kind === 'steam' ? 0.28 : 1,
        steam: b.kind === 'steam',
        grav: isFire ? FIRE_GRAV : GRAV,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.parts.length - 1; i >= 0; i--) {
      const p = this.parts[i];
      p.life += dt;
      const t = p.life / p.max;
      if (t >= 1) {
        this.group.remove(p.mesh);
        (p.mesh.material as THREE.Material).dispose();
        this.parts.splice(i, 1);
        continue;
      }
      p.vel.y -= p.grav * dt;
      p.vel.multiplyScalar(0.96);
      p.mesh.position.addScaledVector(p.vel, dt);
      p.mesh.rotation.x += p.spin.x * dt;
      p.mesh.rotation.y += p.spin.y * dt;
      p.mesh.rotation.z += p.spin.z * dt;
      const mat = p.mesh.material as THREE.MeshStandardMaterial;
      mat.opacity = p.baseOpacity * (1 - t);
      if (p.steam) p.mesh.scale.setScalar(p.baseScale * (1 + t * 1.6)); // stoom dijt uit
    }
  }

  get done(): boolean {
    return this.parts.length === 0;
  }

  clear(): void {
    for (const p of this.parts) {
      this.group.remove(p.mesh);
      (p.mesh.material as THREE.Material).dispose();
    }
    this.parts = [];
  }
}

/** Tapering swoosh-ribbon die een anker-punt (bv. de prikker-tip) volgt. */
export class Trail {
  readonly group = new THREE.Group();
  private pts: THREE.Vector3[] = [];
  private readonly max: number;
  private readonly width: number;
  private readonly geom = new THREE.BufferGeometry();
  private readonly pos: Float32Array;
  private readonly mesh: THREE.Mesh;
  private readonly tan = new THREE.Vector3();
  private readonly perp = new THREE.Vector3();
  private readonly refUp = new THREE.Vector3(0, 0, 1);

  constructor(color: number, width: number, max = 16) {
    this.max = max;
    this.width = width;
    this.pos = new Float32Array(max * 2 * 3);
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    const idx: number[] = [];
    for (let i = 0; i < max - 1; i++) {
      const a = i * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geom.setIndex(idx);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.geom, mat);
    this.mesh.frustumCulled = false;
    this.group.add(this.mesh);
  }

  push(p: THREE.Vector3): void {
    this.pts.push(p.clone());
    if (this.pts.length > this.max) this.pts.shift();
    this.rebuild();
  }

  private rebuild(): void {
    const n = this.pts.length;
    for (let i = 0; i < this.max; i++) {
      const p = this.pts[Math.min(i, n - 1)] ?? this.pts[n - 1];
      if (!p) {
        this.pos.set([0, 0, 0, 0, 0, 0], i * 6);
        continue;
      }
      const a = this.pts[Math.max(0, i - 1)] ?? p;
      const b = this.pts[Math.min(n - 1, i + 1)] ?? p;
      this.tan.copy(b).sub(a);
      if (this.tan.lengthSq() < 1e-8) this.tan.set(1, 0, 0);
      this.perp.copy(this.tan).cross(this.refUp).normalize();
      const taper = (i / Math.max(1, this.max - 1)) * this.width; // breed aan de kop, 0 aan de staart
      this.pos[i * 6] = p.x + this.perp.x * taper;
      this.pos[i * 6 + 1] = p.y + this.perp.y * taper;
      this.pos[i * 6 + 2] = p.z + this.perp.z * taper;
      this.pos[i * 6 + 3] = p.x - this.perp.x * taper;
      this.pos[i * 6 + 4] = p.y - this.perp.y * taper;
      this.pos[i * 6 + 5] = p.z - this.perp.z * taper;
    }
    (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.geom.computeBoundingSphere();
  }

  clear(): void {
    this.pts = [];
    this.pos.fill(0);
    (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }
}

/**
 * Voert een FxSpec uit: voedt de trail met de prop-tip en vuurt bursts op hun
 * swing-fase. Gedeeld door de game-director én het lab (zelfde FX-look).
 */
export class CinematicFxRunner {
  readonly group = new THREE.Group();
  private trail: Trail | null = null;
  private bursts: Burst[] = [];
  private fired: boolean[] = [];
  private prevU = 0;

  constructor(private spec: FxSpec | undefined) {
    if (spec?.trail) {
      this.trail = new Trail(spec.trail.color, spec.trail.width);
      this.group.add(this.trail.group);
    }
    this.fired = (spec?.bursts ?? []).map(() => false);
  }

  /** Per frame: u = swing-fase, tip = prop-tip (of null), origin = burst-oorsprong. */
  update(u: number, dt: number, tip: THREE.Vector3 | null, origin: THREE.Vector3): void {
    if (!this.spec) return;
    // Loop in het lab: u springt terug → reset bursts + trail voor een nieuwe ronde.
    if (u < this.prevU - 0.05) {
      this.fired = this.fired.map(() => false);
      this.trail?.clear();
    }
    this.prevU = u;
    if (this.trail && tip) this.trail.push(tip);
    const specs = this.spec.bursts ?? [];
    for (let i = 0; i < specs.length; i++) {
      if (!this.fired[i] && u >= specs[i].u) {
        this.fired[i] = true;
        const burst = new Burst();
        burst.spawn(origin, specs[i], i + 1);
        this.group.add(burst.group);
        this.bursts.push(burst);
      }
    }
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      this.bursts[i].update(dt);
      if (this.bursts[i].done) {
        this.group.remove(this.bursts[i].group);
        this.bursts.splice(i, 1);
      }
    }
  }

  clear(): void {
    this.trail?.clear();
    for (const b of this.bursts) {
      b.clear();
      this.group.remove(b.group);
    }
    this.bursts = [];
    this.fired = this.fired.map(() => false);
    this.prevU = 0;
  }
}

function frac(x: number): number {
  return x - Math.floor(x);
}
